import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Loader } from '@/components/loaders/Loader'
import { closeModal } from '@/reducers/ui/ui.redux'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import {
  formatSavedFilterFormExistingValues,
  getSavedFilterFormDefaultValues,
} from '@/reducers/savedFilters/savedFilters.helpers'
import {
  createSavedFilter,
  updateSavedFilter,
  fetchFilterFormData,
  fetchVoipCampaignsForFilters,
} from '@/reducers/savedFilters/savedFilters.actions'

import { CALL_FILTERS, MESSAGE_FILTERS } from '@/reducers/savedFilters/savedFilters.constants'
import { CHANNEL_TYPE, MESSAGE_CHANNEL_TYPES } from '@/utils/constants'
import { hasAnyOmnichannelAccess } from '@/utils/helpers'
import { buildSchema } from './SavedFilterForm.schema'

const SavedFilterFormComponent = ({
  currentlyEditingFilter,
  filterFormOptions,
  filterType,
  currentChannel,
  orgActiveChannels,
}) => {
  const dispatch = useDispatch()
  const [clearVoipCampaigns, setClearVoipCampaigns] = useState(false)
  const [selectedChannel, setSelectedChannel] = useState(currentChannel || CHANNEL_TYPE.CALL)
  const [selectedScorecards, setSelectedScorecards] = useState(
    currentlyEditingFilter?.filters?.scorecards || []
  )
  const formikHelpersRef = useRef(null)
  const isMountedRef = useRef(true)
  const { data: savedFiltersData, loading } = useSelector((state) => state.savedFilters)
  const {
    organizationid: currentUserOrgId,
    edit_qa: editQA,
    edit_qa_copilot: editQACopilot,
  } = useSelector((state) => state.currentUser)
  const { qa_enabled: qaEnabled, qa_copilot_enabled: qaCopilotAccess } = useSelector(
    (state) => state.currentOrganization
  )

  const dispositionsOptions = useSelector((state) => state.callSearch.data.dispositionsOptions)
  const scorecardFiltersHidden =
    currentUserOrgId !== 1 && !((qaEnabled && editQA) || (qaCopilotAccess && editQACopilot))
  const { active_channels: currentOrgActiveChannels = [] } = useSelector(
    (state) => state.currentOrganization
  )
  const activeChannels = orgActiveChannels || currentOrgActiveChannels

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  if (scorecardFiltersHidden) {
    filterFormOptions = filterFormOptions.filter(
      (option) =>
        !(
          option.includes('scorecards') ||
          option.includes('scorecardThreshold') ||
          option.includes('scoredStatus')
        )
    )
  }

  const existingValues = currentlyEditingFilter
    ? formatSavedFilterFormExistingValues(currentlyEditingFilter, filterType)
    : getSavedFilterFormDefaultValues(filterType, selectedChannel)

  // Update selected channel when editing a filter
  useEffect(() => {
    if (currentlyEditingFilter?.filters?.channel) {
      setSelectedChannel(currentlyEditingFilter.filters.channel)
    } else if (currentChannel) {
      setSelectedChannel(currentChannel)
    }
  }, [currentlyEditingFilter, currentChannel])

  const handleDateChangeSideEffect = (dateRange) => {
    if (filterFormOptions.includes('voipCampaigns')) {
      dispatch(
        fetchVoipCampaignsForFilters(existingValues.organization || currentUserOrgId, dateRange)
      )
      // clearing of selected VOIP campaigns
      setClearVoipCampaigns(true)
    }
  }

  const handleChannelChangeSideEffect = useCallback((channel) => {
    setSelectedChannel(channel)

    if (formikHelpersRef.current?.setFieldValue && isMountedRef.current) {
      const helpers = formikHelpersRef.current

      // Message channel (Email, SMS, Chat)
      if (MESSAGE_CHANNEL_TYPES.includes(channel)) {
        CALL_FILTERS.forEach(({ name, defaultValue }) => {
          if (isMountedRef.current) {
            helpers.setFieldValue(name, defaultValue)
          }
        })
      }
      // Call channel
      if (channel === CHANNEL_TYPE.CALL) {
        MESSAGE_FILTERS.forEach(({ name, defaultValue }) => {
          if (isMountedRef.current) {
            helpers.setFieldValue(name, defaultValue)
          }
        })
      }
    }
  }, [])

  // handle clearing VOIP campaigns when date range changes
  useEffect(() => {
    if (clearVoipCampaigns && formikHelpersRef.current?.setFieldValue && isMountedRef.current) {
      formikHelpersRef.current.setFieldValue('voipCampaigns', [])
      setClearVoipCampaigns(false)
    }
  }, [clearVoipCampaigns])

  // callback to store Formik helpers without causing re-renders
  const handleFormikReady = useCallback((helpers) => {
    formikHelpersRef.current = helpers
  }, [])

  const handleScorecardChange = (scorecards) => {
    setSelectedScorecards(scorecards || [])
  }

  const handleClose = () => {
    dispatch(closeModal())
  }

  const handleSubmit = async (filter, { setSubmitting }) => {
    const processedFilter = { ...filter }
    if (Array.isArray(processedFilter.dispositions)) {
      processedFilter.dispositions = {
        includes: processedFilter.dispositions.includes,
        selected: processedFilter.dispositions.map(({ value }) => ({ value })),
      }
    }

    if (currentlyEditingFilter) {
      await dispatch(updateSavedFilter(currentlyEditingFilter.uuid, processedFilter, filterType))
    } else {
      await dispatch(createSavedFilter(processedFilter, filterType))
    }

    if (isMountedRef.current) {
      setSubmitting(false)
    }
  }

  const data = {
    ...savedFiltersData,
    dispositionsOptions:
      dispositionsOptions && dispositionsOptions.length ? dispositionsOptions : [],
    isOmnichannelEnabled: hasAnyOmnichannelAccess(activeChannels, currentUserOrgId === 1),
    currentChannel: selectedChannel,
    activeChannels,
    isBaltoAdmin: currentUserOrgId === 1,
  }

  const schema = buildSchema(
    data,
    loading,
    filterFormOptions,
    existingValues,
    handleDateChangeSideEffect,
    handleChannelChangeSideEffect,
    selectedScorecards,
    handleScorecardChange,
    filterType
  )

  useEffect(() => {
    dispatch(
      fetchFilterFormData(currentUserOrgId, filterFormOptions, currentlyEditingFilter, filterType)
    )
  }, [])

  if (loading[filterType]) {
    return <Loader centered />
  }

  return (
    <AbstractForm
      onSubmit={handleSubmit}
      existingValues={existingValues}
      schema={schema}
      buttonLabel="Save"
      onClose={handleClose}
      isModal
      onFormikReady={handleFormikReady}
    />
  )
}

export const SavedFilterForm = SavedFilterFormComponent
